import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { eur, formatApiError } from "@/lib/api";
import { PageHeader, Btn, Modal, Field, inputCls } from "@/components/common";
import { Plus, Pencil, Trash2, Building2, Sparkles } from "lucide-react";

const empty = { name: "", rate_adult: 100, rate_child_5_12: 50, rate_child_2_4: 0, rate_child_0_2: 0, manual: false };

const rAdult = (a) => a.rate_adult ?? a.commission ?? 100;
const r512 = (a) => a.rate_child_5_12 ?? 50;
const r24 = (a) => a.rate_child_2_4 ?? 0;
const r02 = (a) => a.rate_child_0_2 ?? 0;

export default function Agenzie() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/agencies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (a) => {
    setForm({ name: a.name, rate_adult: rAdult(a), rate_child_5_12: r512(a), rate_child_2_4: r24(a), rate_child_0_2: r02(a), manual: a.manual });
    setEditId(a.id); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, commission: Number(form.rate_adult), rate_adult: Number(form.rate_adult),
      rate_child_5_12: Number(form.rate_child_5_12), rate_child_2_4: Number(form.rate_child_2_4),
      rate_child_0_2: Number(form.rate_child_0_2), manual: !!form.manual,
    };
    try {
      if (editId) await api.put(`/agencies/${editId}`, payload);
      else await api.post("/agencies", payload);
      toast.success("Agenzia salvata");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare l'agenzia?")) return;
    try {
      await api.delete(`/agencies/${id}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Agenzie" subtitle="Origini prenotazione e tariffe Maldive Autentiche per fascia d'età">
        <Btn data-testid="add-agency-btn" onClick={openNew}><Plus size={16} /> Nuova agenzia</Btn>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto max-w-4xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-2.5">Agenzia / Origine</th>
              <th className="px-4 py-2.5">Tipo</th>
              <th className="px-3 py-2.5 text-right">Adulti 13+</th>
              <th className="px-3 py-2.5 text-right">5–12</th>
              <th className="px-3 py-2.5 text-right">2–4</th>
              <th className="px-3 py-2.5 text-right">0–1</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody data-testid="agencies-table">
            {items.map((a) => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">
                  <span className="flex items-center gap-2"><Building2 size={15} className="text-slate-400" /> {a.name}</span>
                </td>
                <td className="px-4 py-3">
                  {a.manual ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                      <Sparkles size={11} /> Manuale
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">Maldive Autentiche</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right tabular font-medium">{eur(rAdult(a))}{a.manual ? " +man." : ""}</td>
                <td className="px-3 py-3 text-right tabular text-slate-600">{eur(r512(a))}</td>
                <td className="px-3 py-3 text-right tabular text-slate-600">{eur(r24(a))}</td>
                <td className="px-3 py-3 text-right tabular text-slate-600">{eur(r02(a))}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => openEdit(a)} className="p-1.5 text-slate-400 hover:text-sky-600" data-testid={`edit-agency-${a.id}`}><Pencil size={15} /></button>
                    {a.name !== "Diretto" && (
                      <button data-testid={`del-agency-${a.id}`} onClick={() => remove(a.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-3 max-w-4xl">
        Le tariffe indicano gli importi (€/persona) versati a <b>Maldive Autentiche</b> per le strutture MA (Blu Lodge Maldives, Vaagali Inn),
        suddivisi per fascia d'età: <b>Adulti 13+</b>, <b>5–12</b>, <b>2–4</b>, <b>0–1</b> anni. Le agenzie <b>manuali</b> (es. Dea Maldives)
        hanno in più una voce di spesa dedicata alimentata dalla commissione inserita in prenotazione.
      </p>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica agenzia" : "Nuova agenzia"}>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nome agenzia / origine">
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="a-name" />
          </Field>
          <Field label="Commissione manuale?">
            <select value={form.manual ? "1" : "0"} onChange={(e) => set("manual", e.target.value === "1")} className={inputCls} data-testid="a-manual">
              <option value="0">No — solo tariffe fisse in Maldive Autentiche</option>
              <option value="1">Sì — commissione inserita in prenotazione, voce dedicata</option>
            </select>
          </Field>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-xs font-semibold text-slate-600 mb-2">Tariffe Maldive Autentiche (€/persona) per fascia d'età</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Adulti 13+">
                <input type="number" step="0.01" min="0" value={form.rate_adult} onChange={(e) => set("rate_adult", e.target.value)} className={inputCls} data-testid="a-rate-adult" />
              </Field>
              <Field label="Bambini 5–12">
                <input type="number" step="0.01" min="0" value={form.rate_child_5_12} onChange={(e) => set("rate_child_5_12", e.target.value)} className={inputCls} data-testid="a-rate-512" />
              </Field>
              <Field label="Bambini 2–4">
                <input type="number" step="0.01" min="0" value={form.rate_child_2_4} onChange={(e) => set("rate_child_2_4", e.target.value)} className={inputCls} data-testid="a-rate-24" />
              </Field>
              <Field label="Bambini 0–1">
                <input type="number" step="0.01" min="0" value={form.rate_child_0_2} onChange={(e) => set("rate_child_0_2", e.target.value)} className={inputCls} data-testid="a-rate-02" />
              </Field>
            </div>
          </div>
          {form.manual && (
            <p className="text-xs text-violet-700 bg-violet-50 rounded-md px-3 py-2">
              Oltre alle tariffe qui sopra (versate a Maldive Autentiche), la commissione dedicata di questa agenzia viene inserita in fase di prenotazione.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Annulla</Btn>
            <Btn type="submit" data-testid="save-agency">Salva</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
